// src/components/common/MentionInput.tsx
// Input avec autocomplétion des mentions @utilisateur
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  TextField,
  Popper,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  CircularProgress,
  TextFieldProps,
} from '@mui/material';
import { debounce } from 'lodash';
import api from '../../services/api';

interface User {
  id: number;
  name: string;
  full_name?: string;
  profile_picture?: string;
  avatar_url?: string;
}

interface MentionInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string, mentions: number[]) => void;
  placeholder?: string;
  maxRows?: number;
  minRows?: number;
  onSubmit?: () => void;
  disabled?: boolean;
}

const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder = "Écrire un message...",
  maxRows = 4,
  minRows = 1,
  onSubmit,
  disabled = false,
  ...textFieldProps
}) => {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionPosition, setMentionPosition] = useState<{ start: number; end: number } | null>(null);
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionedUsers, setMentionedUsers] = useState<number[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const popperAnchorRef = useRef<HTMLDivElement>(null);

  // Rechercher les utilisateurs
  const searchUsers = useCallback(
    debounce(async (query: string) => {
      if (!query || query.length < 1) {
        setSuggestions([]);
        return;
      }

      setLoadingSuggestions(true);
      try {
        const response = await api.get('/users/search', {
          params: { q: query, limit: 5 },
        });
        setSuggestions(response.data || []);
      } catch (err) {
        console.error('Error searching users:', err);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300),
    []
  );

  // Détecter les mentions dans le texte
  const detectMention = (text: string, cursorPosition: number) => {
    // Trouver le @ le plus proche avant le curseur
    let mentionStart = -1;
    for (let i = cursorPosition - 1; i >= 0; i--) {
      if (text[i] === '@') {
        mentionStart = i;
        break;
      }
      // Si on trouve un espace ou un autre @, on arrête
      if (text[i] === ' ' || text[i] === '\n') {
        break;
      }
    }

    if (mentionStart === -1) {
      return null;
    }

    // Vérifier si @ est au début ou après un espace
    if (mentionStart > 0 && text[mentionStart - 1] !== ' ' && text[mentionStart - 1] !== '\n') {
      return null;
    }

    const query = text.slice(mentionStart + 1, cursorPosition);

    // Ne pas matcher si le query contient des espaces (mention terminée)
    if (query.includes(' ')) {
      return null;
    }

    return {
      query,
      start: mentionStart,
      end: cursorPosition,
    };
  };

  // Gérer le changement de texte
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    onChange(newValue, mentionedUsers);

    // Détecter si on est en train de mentionner quelqu'un
    const mention = detectMention(newValue, cursorPosition);

    if (mention) {
      setMentionQuery(mention.query);
      setMentionPosition({ start: mention.start, end: mention.end });
      searchUsers(mention.query);
      setSelectedIndex(0);
    } else {
      setMentionQuery(null);
      setMentionPosition(null);
      setSuggestions([]);
    }
  };

  // Sélectionner une suggestion
  const selectSuggestion = (user: User) => {
    if (!mentionPosition) return;

    const userName = user.full_name || user.name;
    const beforeMention = value.slice(0, mentionPosition.start);
    const afterMention = value.slice(mentionPosition.end);
    const newValue = `${beforeMention}@${userName} ${afterMention}`;

    // Ajouter l'utilisateur à la liste des mentions
    const newMentions = [...mentionedUsers];
    if (!newMentions.includes(user.id)) {
      newMentions.push(user.id);
    }
    setMentionedUsers(newMentions);

    onChange(newValue, newMentions);
    setMentionQuery(null);
    setMentionPosition(null);
    setSuggestions([]);

    // Focus l'input et placer le curseur après la mention
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = beforeMention.length + userName.length + 2;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  // Gérer les touches clavier
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (suggestions.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          selectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setMentionQuery(null);
        setSuggestions([]);
        break;
    }
  };

  // Fermer les suggestions si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popperAnchorRef.current && !popperAnchorRef.current.contains(e.target as Node)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isOpen = suggestions.length > 0 || loadingSuggestions;

  return (
    <Box ref={popperAnchorRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        {...textFieldProps}
        inputRef={inputRef}
        fullWidth
        multiline
        minRows={minRows}
        maxRows={maxRows}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        InputProps={{
          ...textFieldProps.InputProps,
          sx: {
            borderRadius: 5,
            bgcolor: '#f0f2f5',
            ...((textFieldProps.InputProps as any)?.sx || {}),
          },
        }}
      />

      <Popper
        open={isOpen}
        anchorEl={popperAnchorRef.current}
        placement="top-start"
        style={{ zIndex: 1300 }}
      >
        <Paper
          elevation={8}
          sx={{
            width: popperAnchorRef.current?.offsetWidth || 300,
            maxHeight: 250,
            overflow: 'auto',
            mb: 1,
            borderRadius: 2,
          }}
        >
          {loadingSuggestions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : suggestions.length === 0 && mentionQuery ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Aucun utilisateur trouvé
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0 }}>
              {suggestions.map((user, index) => (
                <ListItem
                  key={user.id}
                  onClick={() => selectSuggestion(user)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: index === selectedIndex ? 'action.selected' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={user.profile_picture || user.avatar_url}
                      sx={{ width: 32, height: 32 }}
                    >
                      {(user.full_name || user.name).charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.full_name || user.name}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Popper>
    </Box>
  );
};

// Fonction utilitaire pour parser les mentions dans le texte
export const parseMentions = (text: string): string[] => {
  const mentionRegex = /@(\w+(?:\s\w+)?)/g;
  const mentions: string[] = [];
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push(match[1]);
  }
  return mentions;
};

// Composant pour afficher le texte avec mentions stylisées
export const MentionText: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(/(@\w+(?:\s\w+)?)/g);

  return (
    <Typography component="span" sx={{ whiteSpace: 'pre-wrap' }}>
      {parts.map((part, index) => {
        if (part.startsWith('@')) {
          return (
            <Typography
              key={index}
              component="span"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {part}
            </Typography>
          );
        }
        return part;
      })}
    </Typography>
  );
};

export default MentionInput;
